import React from "react";
import { Link } from "react-router-dom";
import Marquee from "@/components/Marquee";
import CTABand from "@/components/CTABand";
import SectionLabel from "@/components/SectionLabel";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import Crown from "@/components/Crown";
import CTAMenu from "@/components/CTAMenu";
import FreebieInline from "@/components/FreebieInline";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import ASSETS from "@/lib/assets";

const PROGRAM_ITEMS = [
  { to: "/programs#whisper", label: "Whisper", sublabel: "Awakening · 8 weeks" },
  { to: "/programs#grit", label: "GRIT™", sublabel: "Transformation · 12 weeks" },
  { to: "/programs#sparkle", label: "Sparkle", sublabel: "Visibility · 10 weeks" },
  { to: "/programs#ascend", label: "Ascend", sublabel: "Legacy · 12 weeks" },
];

const COACHING_ITEMS = [
  { to: "/coaching#personal-dev", label: "Personal Development", sublabel: "Identity · values · alignment" },
  { to: "/coaching#leadership", label: "Leadership Development", sublabel: "Decisions · presence · teams" },
  { to: "/coaching#career", label: "Career Coaching", sublabel: "Pivot · promotion · exit" },
  { to: "/coaching#business", label: "Business Coaching", sublabel: "Strategy + identity for founders" },
  { to: "/coaching#brand", label: "Brand & Marketing", sublabel: "Voice · positioning · visibility" },
];

const Home = () => {
  return (
    <div data-testid="page-home">
      <ExitIntentPopup />
      {/* HERO */}
      <section className="hero">
        <div className="hero-grid">
          <div className="hero-content">
            <SectionLabel gold>A QUIET REVOLUTION FOR HIGH-PERFORMING WOMEN</SectionLabel>
            <h1>
              You built the life. <em>Now return to you.</em>
            </h1>
            <p className="hero-tagline">
              “You don’t have to choose between success and yourself.”
            </p>
            <p className="hero-sub">
              Identity coaching for women who are accomplished on paper and unrecognizable to themselves.
            </p>
            <div className="hero-cta-row">
              <Link to="/contact" className="btn btn-primary" data-testid="hero-apply">
                Apply Now →
              </Link>
              <CTAMenu label="Explore Programs" items={PROGRAM_ITEMS} testId="hero-menu-programs" />
              <CTAMenu label="Explore Private 1:1" items={COACHING_ITEMS} testId="hero-menu-coaching" />
            </div>
          </div>
          <div className="hero-photo">
            <PhotoPlaceholder src={ASSETS.photoPinkBlazer} position="center 22%" label="Nijah Almond — pink blazer portrait" />
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="stats-band" data-testid="stats-band">
        <div className="container-x">
          <div className="stats-grid">
            <div className="stat" data-testid="stat-women">
              <div className="num">100<em>+</em></div>
              <div className="label">Founders, Professionals, Entrepreneurs</div>
            </div>
            <div className="stat" data-testid="stat-quote">
              <p className="signature-quote">
                “You don’t have to choose between success and yourself.”
              </p>
            </div>
            <div className="stat" data-testid="stat-programs">
              <div className="num">04</div>
              <div className="label">Group Programs</div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* MIRROR / problem */}
      <section className="mirror" data-testid="home-mirror">
        <div className="mirror-inner">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Crown size={36} color="#054452" opacity={0.55} />
          </div>
          <SectionLabel center>THE QUIET TRUTH</SectionLabel>
          <p className="mirror-quote">
            “You can do everything right and still feel like you’re not living your own life.”
          </p>
          <p className="body-base" style={{ maxWidth: 620, margin: "0 auto" }}>
            The titles. The team. The check-mark resume. And underneath it — a woman who can’t remember
            the last time she chose something only because it was hers.
          </p>
        </div>
      </section>

      {/* ABOUT STRIP — Meet Nijah */}
      <section className="section-pad bg-cream">
        <div className="container-x">
          <div className="split">
            <PhotoPlaceholder src={ASSETS.photoDesk} position="center 30%" label="At the desk" />
            <div>
              <Crown size={32} opacity={0.85} />
              <SectionLabel>MEET NIJAH</SectionLabel>
              <h2 className="section-title text-teal" style={{ marginBottom: 22 }}>
                Coach. Strategist.
                <br />
                <span className="italic-c text-coral">Identity Architect.</span>
              </h2>
              <p className="body-base">
                Nijah Almond is an identity transformation coach and founder of{" "}
                <strong className="text-teal">Nijah Almond Coaching and Consultancy LLC</strong>{" "}
                and the{" "}
                <strong className="text-teal">GRIT™ Identity Transformation System</strong>
                —a 12-week framework for high-performing women ready to stop performing and
                start returning to themselves.
              </p>
              <p className="body-base">
                Expertise spanning{" "}
                <strong className="text-teal">
                  personal development, leadership, career strategy, business growth, and
                  brand building
                </strong>
                —meeting every client at the intersection of ambition and alignment.
              </p>

              <div className="grit-callout" data-testid="grit-callout">
                <div className="grit-callout-eyebrow">THE GRIT™ METHOD</div>
                <p className="grit-callout-body">
                  Grounded. Resilient. Intentional. Transformed. A 12-week system to reconnect
                  high-performing women with who they actually are.
                </p>
              </div>

              <Link
                to="/about"
                className="btn btn-outline-dark"
                style={{ marginTop: 26 }}
                data-testid="home-about-link"
              >
                Meet Nijah →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS PREVIEW */}
      <section className="section-pad bg-teal">
        <div className="container-x">
          <SectionLabel gold>GROUP PROGRAMS</SectionLabel>
          <h2 className="section-title" style={{ color: "#fff", marginBottom: 36 }}>
            Every woman’s journey <span className="italic-c text-gold">starts somewhere.</span>
          </h2>
          <div className="prog-grid">
            {[
              {
                name: "Whisper",
                tag: "Awakening · Entry",
                desc: "The quiet pull back to yourself. Identity awareness and first steps toward alignment.",
                to: "/programs#whisper",
              },
              {
                name: "GRIT™",
                tag: "Transformation · Flagship",
                desc: "The full 12-week system. Rebuild from the inside out without losing what you’ve built.",
                to: "/programs#grit",
              },
              {
                name: "Sparkle",
                tag: "Visibility · Momentum",
                desc: "Show up, stand out, lead with full presence. Own your voice in every room.",
                to: "/programs#sparkle",
              },
              {
                name: "Ascend",
                tag: "Legacy · Elite",
                desc: "Operate at the highest level. Build legacy, influence, and a life that reflects you.",
                to: "/programs#ascend",
              },
            ].map((p) => (
              <Link
                key={p.name}
                to={p.to}
                className="prog-card"
                data-testid={`prog-card-${p.name.toLowerCase().replace(/[^a-z]/g, "")}`}
              >
                <Crown size={22} opacity={0.9} />
                <div className="ptag">{p.tag}</div>
                <div className="pname">{p.name}</div>
                <div className="pdesc">{p.desc}</div>
                <div className="plink">Learn More →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRIVATE COACHING BAND */}
      <section className="private-band" data-testid="private-band">
        <div className="container-x">
          <div className="private-grid">
            <div>
              <SectionLabel>PRIVATE COACHING</SectionLabel>
              <h2 className="section-title text-teal">
                Want 1:1 private coaching <span className="italic-c text-coral">built entirely around you?</span>
              </h2>
            </div>
            <div>
              <p className="body-base">
                Personalized packages across{" "}
                <strong className="text-teal">
                  personal development, leadership, career, business, and brand &amp; marketing
                </strong>
                . Every session tailored to your specific goals and season.
              </p>
              <Link
                to="/coaching"
                className="btn btn-teal"
                style={{ marginTop: 22 }}
                data-testid="private-coaching-cta"
              >
                Explore 1:1 Coaching →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — The Process */}
      <section className="section-pad bg-cream">
        <div className="container-x">
          <SectionLabel>THE PROCESS</SectionLabel>
          <h2 className="section-title text-teal">How the journey <span className="italic-c text-coral">unfolds.</span></h2>
          <div className="three-col" style={{ marginTop: 28 }}>
            <div className="step">
              <div className="step-num">01</div>
              <h4>Submit Your Application</h4>
              <p className="body-base">
                Applications are open. Submit yours and our team will review your goals,
                season, and vision.
              </p>
            </div>
            <div className="step">
              <div className="step-num">02</div>
              <h4>Your Custom Path</h4>
              <p className="body-base">
                Together we map your personalized transformation plan — built around you,
                not a template.
              </p>
            </div>
            <div className="step">
              <div className="step-num">03</div>
              <h4>Return &amp; Rise</h4>
              <p className="body-base">
                You do the work. We hold the space.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-cream" style={{ paddingTop: 0 }}>
        <div className="container-x">
          <SectionLabel>WHAT WOMEN SAY</SectionLabel>
          <h2 className="section-title text-teal">Truth, after a long time of polish.</h2>
          <div className="three-col" style={{ marginTop: 28 }}>
            {[
              { q: "I came in successful and exhausted. I left finally able to recognize my own voice in the room.", n: "Ayana W.", r: "VP, Tech" },
              { q: "Nijah doesn’t coddle and she doesn’t cut. She just refuses to let you keep performing.", n: "Dr. Lila R.", r: "Founder, Wellness" },
              { q: "GRIT™ rewired the way I lead — at work, at home, with myself. I came back to me.", n: "Marisol P.", r: "Director, Nonprofit" },
            ].map((t, i) => (
              <div key={i} className="tcard" data-testid={`testimonial-${i}`}>
                <Crown size={22} color="#0C6474" opacity={0.5} />
                <q>{t.q}</q>
                <div>
                  <div className="tname">{t.n}</div>
                  <div className="trole">{t.r}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FREEBIE INLINE */}
      <FreebieInline />

      {/* IN THE ROOM & ON THE STAGE */}
      <section className="strip right" data-testid="strip-speaking">
        <div className="strip-photo">
          <PhotoPlaceholder src={ASSETS.photoSpeaking} position="center 28%" label="In the room and on the stage" />
        </div>
        <div className="strip-inner">
          <SectionLabel gold>IN THE ROOM &amp; ON THE STAGE</SectionLabel>
          <h3>A coach who <span className="italic-c text-gold">leads by example.</span></h3>
          <p className="body-base">
            Keynotes, workshops, retreats, and the kind of work that gets named — not because it
            performs well, but because it actually changes the room.
          </p>
          <Link to="/contact" className="btn btn-primary" style={{ marginTop: 10, alignSelf: "flex-start" }} data-testid="strip-speaking-cta">
            Apply to Work With Nijah →
          </Link>
        </div>
      </section>

      <CTABand
        eyebrow="READY TO TAKE THE NEXT STEP?"
        title={<>The woman you’ve been <span className="italic-c text-gold">looking for is you.</span></>}
        sub="Applications are open. Take the first step back to yourself — and forward into everything you’re meant to lead."
        primaryLabel="Apply Now →"
        primaryTo="/contact"
        secondaryLabel="Explore Programs First"
        secondaryTo="/programs"
      />
    </div>
  );
};

export default Home;
